import './HeroSlideCard.css';

/**
 * HeroSlideCard Component
 * Displays a single hero slide with image preview and actions
 *
 * Props:
 * - slide: HeroSlide object from backend API
 * - onEdit: (slide) => void - Opens edit modal
 * - onToggleActive: (id) => void - Toggle active status
 * - isUpdating: boolean - Disable actions while updating
 * - isDragging: boolean - Visual state for drag-and-drop
 * - dragHandleProps: object - Props for drag handle element
 * - isLastActive: boolean - Whether this is the only active slide
 */
function HeroSlideCard({
  slide,
  onEdit,
  onToggleActive,
  isUpdating = false,
  isDragging = false,
  dragHandleProps = {},
  isLastActive = false,
}) {
  // Can't deactivate if this is the last active slide
  const canDeactivate = !isLastActive || !slide.isActive;

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isUpdating && canDeactivate) {
      onToggleActive(slide.id);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(slide);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get button style preview class
  const getButtonPreviewClass = () => {
    if (!slide.buttonStyle) return '';
    return `button-preview--${slide.buttonStyle}`;
  };

  return (
    <div
      className={`hero-slide-card ${isDragging ? 'hero-slide-card--dragging' : ''} ${
        !slide.isActive ? 'hero-slide-card--inactive' : ''
      }`}
    >
      {/* Drag Handle */}
      <div className="hero-slide-card__drag-handle" {...dragHandleProps}>
        <span className="drag-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="16"
            height="16"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </span>
        <span className="hero-slide-card__order">Slide {slide.displayOrder}</span>
        <span
          className={`hero-slide-card__status-badge ${
            slide.isActive ? 'status-badge--active' : 'status-badge--inactive'
          }`}
        >
          {slide.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Image Preview */}
      <div
        className="hero-slide-card__image"
        style={{ backgroundImage: `url(${slide.imageUrl})` }}
      >
        <div className="hero-slide-card__overlay">
          <h3 className="hero-slide-card__title">{slide.title}</h3>
          {slide.subtitle && (
            <p className="hero-slide-card__subtitle">{slide.subtitle}</p>
          )}
          {slide.buttonText && (
            <span className={`hero-slide-card__button-preview ${getButtonPreviewClass()}`}>
              {slide.buttonText}
            </span>
          )}
          {slide.readMoreContent && (
            <span className="hero-slide-card__readmore-indicator">
              + Read More Content
            </span>
          )}
        </div>
      </div>

      {/* Card Footer */}
      <div className="hero-slide-card__footer">
        <div className="hero-slide-card__meta">
          <span className="hero-slide-card__updated">
            Updated: {formatDate(slide.updatedAt)}
          </span>
          {slide.buttonLink && (
            <span className="hero-slide-card__link" title={slide.buttonLink}>
              Link: {slide.buttonLink}
            </span>
          )}
        </div>

        <div className="hero-slide-card__actions">
          <button
            type="button"
            className={`btn btn-toggle ${slide.isActive ? 'btn-toggle--active' : ''} ${!canDeactivate ? 'btn-toggle--disabled' : ''}`}
            onClick={handleToggle}
            disabled={isUpdating || !canDeactivate}
            title={
              !canDeactivate
                ? 'At least one slide must be active'
                : slide.isActive
                ? 'Deactivate slide'
                : 'Activate slide'
            }
          >
            {isUpdating ? (
              <span className="btn-spinner" />
            ) : slide.isActive ? (
              'Deactivate'
            ) : (
              'Activate'
            )}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleEdit}
            disabled={isUpdating}
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}

export default HeroSlideCard;
